import { Controller } from '@hotwired/stimulus';

const POLLING_INTERVAL_MS = 3000;
// The signer's redirect URL disables the iframe events (onSuccess never fires): the page watches
// the database for the signature instead.
const WATCH_INTERVAL_MS = 5000;
const PREPARATION_TIMEOUT_MS = 60000;
const SDK_LOAD_TIMEOUT_MS = 15000;
const STATES = ['preparing', 'ready', 'error', 'signed'];

export default class extends Controller {
  static targets = ['preparing', 'ready', 'error', 'signed', 'frame', 'fallbackLink'];

  static values = {
    statusUrl: String,
    preparationUrl: String,
    clientErrorsUrl: String,
    redirectUrl: String,
    sdkUrl: String,
    sdkIntegrity: String,
    isSandbox: Boolean,
  };

  connect() {
    this.cycle = 0;
    this.onVisibilityChange = this.resumeWhenVisible.bind(this);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.start();
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.stop();
  }

  retry() {
    this.start();
  }

  // A loop from an older cycle (retry, disconnect) stops instead of rescheduling itself.
  stop() {
    this.cycle += 1;
    this.pausedPoll = null;
    clearTimeout(this.pollingTimeout);
    this.yousign?.removeMessageListener();
    this.yousign = null;
  }

  isCurrent(cycle) {
    return cycle === this.cycle;
  }

  async start() {
    this.stop();
    const cycle = this.cycle;

    this.show('preparing');
    this.deadline = Date.now() + PREPARATION_TIMEOUT_MS;

    await this.prepare();
    this.poll(cycle);
  }

  async prepare() {
    try {
      await fetch(this.preparationUrlValue, { method: 'POST', headers: this.headers() });
    } catch (error) {
      this.report('preparation_request_failed', error.message);
    }
  }

  async poll(cycle) {
    const status = await this.fetchStatus();

    if (!this.isCurrent(cycle)) return;

    if (status?.state === 'ready') {
      this.showSignature(status.signature_url, cycle);
    } else if (status?.state === 'signed') {
      this.show('signed');
    } else if (Date.now() >= this.deadline) {
      this.report('preparation_timed_out');
      this.show('error');
    } else {
      this.schedule(() => this.poll(cycle), POLLING_INTERVAL_MS);
    }
  }

  schedule(nextPoll, delay) {
    this.pollingTimeout = setTimeout(() => {
      if (document.hidden) {
        this.pausedPoll = nextPoll;
      } else {
        nextPoll();
      }
    }, delay);
  }

  resumeWhenVisible() {
    if (document.hidden || !this.pausedPoll) return;

    const nextPoll = this.pausedPoll;

    this.pausedPoll = null;
    this.deadline = Date.now() + PREPARATION_TIMEOUT_MS;
    nextPoll();
  }

  async watchSignature(cycle) {
    const status = await this.fetchStatus({ watch: true });

    if (!this.isCurrent(cycle)) return;

    if (status?.state === 'signed') {
      this.show('signed');
    } else if (status?.state === 'preparing') {
      // The signature was reset meanwhile: the iframe shows an obsolete request.
      this.start();
    } else {
      this.schedule(() => this.watchSignature(cycle), WATCH_INTERVAL_MS);
    }
  }

  async fetchStatus({ watch = false } = {}) {
    const url = watch ? `${this.statusUrlValue}?watch=1` : this.statusUrlValue;

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });

      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  }

  async showSignature(signatureUrl, cycle) {
    this.fallbackLinkTarget.href = signatureUrl;
    this.frameTarget.classList.remove('d-none');
    this.show('ready');
    this.watchSignature(cycle);

    try {
      await this.loadSdk();
    } catch (error) {
      this.report('sdk_load_failed', error?.message);
      this.frameTarget.classList.add('d-none');
      return;
    }

    if (!this.isCurrent(cycle)) return;

    try {
      const yousign = new window.Yousign({
        signatureLink: signatureUrl,
        iframeContainerId: this.frameTarget.id,
        isSandbox: this.isSandboxValue,
        iframeAttributes: { referrerPolicy: 'strict-origin-when-cross-origin' },
      });

      yousign.onSuccess(() => window.location.replace(this.redirectUrlValue));
      yousign.onError((data) => this.report('yousign_error', JSON.stringify(data)));
      yousign.onDeclined((data) => this.report('yousign_declined', JSON.stringify(data)));
      this.yousign = yousign;
    } catch (error) {
      this.report('sdk_init_failed', error.message);
      this.frameTarget.classList.add('d-none');
    }
  }

  loadSdk() {
    if (window.Yousign) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Yousign SDK load timed out')), SDK_LOAD_TIMEOUT_MS);
      const script = document.createElement('script');

      script.src = this.sdkUrlValue;
      script.integrity = this.sdkIntegrityValue;
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.onload = () => {
        clearTimeout(timeout);
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Yousign SDK could not be loaded'));
      };

      document.head.appendChild(script);
    });
  }

  show(state) {
    STATES.forEach((name) => {
      const target = this[`${name}Target`];

      target.classList.toggle('d-none', name !== state);
      target.classList.toggle('d-flex', name === state);
    });
  }

  report(kind, message = '') {
    fetch(this.clientErrorsUrlValue, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ client_error: { kind, message: String(message ?? '').slice(0, 500) } }),
      keepalive: true,
    }).catch(() => {});
  }

  headers() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
    };
  }
}
